// The Sequence tab's autosave bookkeeping: which edits are waiting on the debounce timer, and
// which save is out now. Plain values and functions, so the flush on leaving is tested without React.
//
// Edits save a moment after the last keystroke. Leaving before that moment (closing the panel,
// switching account, reloading the page) flushes the waiting edits at once instead of losing them.

import type { SequenceDraft } from '../api/types.ts';
import { personalLinesWithLiquid } from './sequenceBuilder.ts';
import type { BuilderState } from './sequenceBuilder.ts';
import { unknownPrefix } from './campaignTypes.ts';

export interface AutosaveTracker {
  // The edited builder the debounce timer will save; null when nothing is waiting.
  pending: BuilderState | null;
  // The save that is out now, the state it is saving, and the draft it resolves to (null if it failed).
  inFlight: Promise<SequenceDraft | null> | null;
  inFlightFrom: BuilderState | null;
}

export function emptyTracker(): AutosaveTracker {
  return { pending: null, inFlight: null, inFlightFrom: null };
}

// Whether this builder may save on its own: edited, unlocked, worth a draft, and saveable. A state
// whose save just failed waits for the next edit or "Retry save".
export function autosaveReady(state: BuilderState, failedSave: BuilderState | null): boolean {
  if (!state.dirty || state.locked || state === failedSave) return false;
  if (!state.steps.length && !state.draftId) return false;
  return personalLinesWithLiquid(state).length === 0 && unknownPrefix(state.name) === null;
}

// Record a save going out, so a flush that follows it waits for it and reuses its draft id.
export function trackSave(tracker: AutosaveTracker, state: BuilderState,
                          saving: Promise<SequenceDraft | null>): void {
  tracker.inFlight = saving;
  tracker.inFlightFrom = state;
  if (tracker.pending === state) tracker.pending = null;
}

// A tracked save came back: forget it, unless a later save has taken its place.
export function untrackSave(tracker: AutosaveTracker, saving: Promise<SequenceDraft | null>): void {
  if (tracker.inFlight !== saving) return;
  tracker.inFlight = null;
  tracker.inFlightFrom = null;
}

// Saves the waiting edits now. A save still out is waited for first, so the newer edits land last;
// when it was a new draft's first save, the edits go under its id instead of making a second draft.
// Resolves to the saved draft, or null when nothing was waiting (or it is the state being saved).
export async function flushPendingSave(
  tracker: AutosaveTracker,
  saveDraft: (state: BuilderState) => Promise<SequenceDraft>,
): Promise<SequenceDraft | null> {
  const waiting = tracker.pending;
  tracker.pending = null;
  if (waiting === null || waiting === tracker.inFlightFrom) return null;
  const previousSave = tracker.inFlight;
  const previousWasNewDraft = tracker.inFlightFrom !== null && tracker.inFlightFrom.draftId === null;

  // Wait out the save that is already going; take its id if both are the same new draft.
  let state = waiting;
  if (previousSave !== null) {
    const previousDraft = await previousSave;
    if (previousDraft !== null && previousWasNewDraft && state.draftId === null) {
      state = { ...state, draftId: previousDraft.draft_id };
    }
  }
  return saveDraft(state);
}
